import invariant from 'invariant';
import cloneDeep from 'lodash.clonedeep';

import {
  Org as ApiOrg,
  addAdmin as apiAddAdmin,
  addOwner as apiAddOwner,
  approveAdminRequest as apiApproveAdminRequest,
  deleteOrg as apiDeleteOrg,
  fetchOrg as apiFetchOrg,
  rejectAdminRequest as apiRejectAdminRequest,
  removeAdmin as apiRemoveAdmin,
} from 'api/orgAdmin';

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line import/no-cycle, no-restricted-imports
import { Dispatch, GetState } from '../..';
// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line import/no-cycle, no-restricted-imports
import { updateCurrentUser } from '../currentUser';
// eslint-disable-next-line no-restricted-imports
import { setError } from '../error';

// Types

export type Org = ApiOrg & {
  isFetching: boolean;
};
export type State = {
  byId: Record<string, Org>;
};

type FetchOrgRequestAction = {
  type: 'ORG_DASHBOARD_FETCH_ORG_REQUEST';
  orgId: string;
};
type FetchOrgSuccessAction = {
  type: 'ORG_DASHBOARD_FETCH_ORG_SUCCESS';
  org: ApiOrg;
};
type DeleteOrgAction = {
  type: 'ORG_DASHBOARD_DELETE_ORG';
  orgId: string;
};
type PartialUpdateOrgAction = {
  type: 'ORG_DASHBOARD_PARTIAL_UPDATE_ORG';
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  values: Record<string, any>;
};
export type Action =
  | FetchOrgRequestAction
  | FetchOrgSuccessAction
  | PartialUpdateOrgAction
  | DeleteOrgAction;

// Reducer

// eslint-disable-next-line import/no-default-export
export default function orgsReducer(
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/default-param-last
  state: State = {
    byId: {},
  },
  action: Action
) {
  switch (action.type) {
    case 'ORG_DASHBOARD_FETCH_ORG_REQUEST':
      return {
        byId: {
          ...state.byId,
          [action.orgId]: {
            ...state.byId[action.orgId],
            id: action.orgId,
            isFetching: true,
          },
        },
      };

    case 'ORG_DASHBOARD_FETCH_ORG_SUCCESS':
      return {
        byId: {
          ...state.byId,
          [action.org.id]: { ...action.org, isFetching: false },
        },
      };

    case 'ORG_DASHBOARD_DELETE_ORG': {
      const newState = { ...state };
      delete newState.byId[action.orgId];
      return newState;
    }

    case 'ORG_DASHBOARD_PARTIAL_UPDATE_ORG': {
      return {
        byId: {
          ...state.byId,
          [action.values.id]: {
            ...state.byId[action.values.id],
            ...action.values,
          },
        },
      };
    }

    default:
      return state;
  }
}

// Actions

const fetchOrgRequest = (orgId: string): FetchOrgRequestAction => ({
  type: 'ORG_DASHBOARD_FETCH_ORG_REQUEST',
  orgId,
});

const fetchOrgSuccess = (org: ApiOrg): FetchOrgSuccessAction => ({
  type: 'ORG_DASHBOARD_FETCH_ORG_SUCCESS',
  org,
});

export const fetchOrg =
  (orgId: string, force = false) =>
  (dispatch: Dispatch, getState: GetState) => {
    const state = getState();
    const org = state.orgDashboard.orgs.byId[orgId];

    if (!org || force) {
      dispatch(fetchOrgRequest(orgId));
      apiFetchOrg(orgId)
        .then((fetchedOrg) => {
          dispatch(fetchOrgSuccess(fetchedOrg));
        })
        .catch((error) => {
          dispatch(setError(error));
        });
    }
  };
export const updateOrg = fetchOrgSuccess;
export const deleteOrg =
  (orgId: string) => (dispatch: Dispatch, getState: GetState) => {
    const currentUser = cloneDeep(getState().currentUser);
    currentUser.orgs = (currentUser.orgs || []).filter((o) => o.id !== orgId);
    return apiDeleteOrg(orgId)
      .then(() => {
        dispatch({
          type: 'ORG_DASHBOARD_DELETE_ORG',
          orgId,
        });
        dispatch(updateCurrentUser(currentUser));
      })
      .catch((error) => {
        dispatch(setError(error));
      });
  };
export const addAdmin =
  (orgId: string, email: string, isOwner: boolean) =>
  (dispatch: Dispatch, getState: GetState) => {
    const add = isOwner ? apiAddOwner : apiAddAdmin;
    return add(orgId, email).then(({ user, invite }) => {
      const org = getState().orgDashboard.orgs.byId[orgId];
      invariant(org, 'Invalid org id');

      if (user) {
        if (!org.admins.find((a) => a.id === user.id)) {
          dispatch(updateOrg({ ...org, admins: [...org.admins, user] }));
        } else {
          dispatch(
            updateOrg({
              ...org,
              admins: [user, ...org.admins.filter((a) => a.id !== user.id)],
            })
          );
        }
      } else if (invite) {
        if (!org.invites.find((i) => i.id === invite.id)) {
          dispatch(updateOrg({ ...org, invites: [...org.invites, invite] }));
        }
      }

      return {
        user,
        invite,
      };
    });
  };
export const makeOwner =
  (orgId: string, userId: string) =>
  (dispatch: Dispatch, getState: GetState) => {
    const org = getState().orgDashboard.orgs.byId[orgId];
    const admin = org.admins.find((a) => a.id === userId);
    invariant(admin, 'User is not an admin');
    return apiAddOwner(orgId, admin.email).then(({ user }) => {
      invariant(user, 'Missing user');
      dispatch(
        updateOrg({
          ...org,
          admins: [user, ...org.admins.filter((a) => a.id !== user.id)],
        })
      );
    });
  };
export const removeAdmin =
  (orgId: string, userId: string) =>
  (dispatch: Dispatch, getState: GetState) => {
    const state = getState();
    const currentUser = cloneDeep(state.currentUser);
    currentUser.orgs = (currentUser.orgs || []).filter((o) => o.id !== orgId);
    const org = state.orgDashboard.orgs.byId[orgId];
    const admin = org.admins.find((a) => a.id === userId);
    invariant(admin, 'User is not an admin');
    return apiRemoveAdmin(orgId, admin.id).then(() => {
      dispatch(
        updateOrg({
          ...org,
          admins: org.admins.filter((a) => a.id !== admin.id),
        })
      );

      if (currentUser.id === admin.id) {
        dispatch(updateCurrentUser(currentUser));
      }
    });
  };
export const approveAdminRequest =
  (orgId: string, requestId: string) =>
  (dispatch: Dispatch, getState: GetState) => {
    const org = getState().orgDashboard.orgs.byId[orgId];
    const adminRequest = org.adminRequests.find((r) => r.id === requestId);
    invariant(adminRequest, 'No admin request found');
    return apiApproveAdminRequest(orgId, requestId).then((admin) => {
      dispatch(
        updateOrg({
          ...org,
          adminRequests: org.adminRequests.filter((r) => r.id !== requestId),
          admins: [admin, ...org.admins],
        })
      );
    });
  };
export const rejectAdminRequest =
  (orgId: string, requestId: string) =>
  (dispatch: Dispatch, getState: GetState) => {
    const org = getState().orgDashboard.orgs.byId[orgId];
    const adminRequest = org.adminRequests.find((r) => r.id === requestId);
    invariant(adminRequest, 'No admin request found');
    return apiRejectAdminRequest(orgId, requestId).then(() => {
      dispatch(
        updateOrg({
          ...org,
          adminRequests: org.adminRequests.filter((r) => r.id !== requestId),
        })
      );
    });
  };
export const partialUpdateOrg = (
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  values: Record<string, any>
): PartialUpdateOrgAction => ({
  type: 'ORG_DASHBOARD_PARTIAL_UPDATE_ORG',
  values,
});
